import { ConnectError } from '@bufbuild/connect';
import cn from 'classnames';
import { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { AuthState_AuthStatus } from '@/shared/api/protocol-ts/api/auth/dto_auth_pb';
import smileCat from '@/shared/assets/images/cats/smile.png';
import { PATHS, UserBadRequestError } from '@/shared/config';
import { useAppDispatch, useMedia } from '@/shared/hooks';
import { safeJSONParse, trackEvent } from '@/shared/lib';
import { Icon, Spinner } from '@/shared/ui';

import { authModel } from '@/entities/auth';

import { errorToastCaller } from '@/features/errorToastCaller';

import { MobileHeader } from '@/widgets/Header';

import styles from './ConfirmEmail.module.scss';

type ConfirmEmailProps = {
  className?: string;
  testID?: string;
};

export const ConfirmEmail: FC<ConfirmEmailProps> = (props) => {
  const { className, testID } = props;

  const location = useLocation();

  const email = (location?.state as { email: string })?.email;

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isMobile } = useMedia();

  const [searchParams] = useSearchParams();

  const [isVerifiedSuccessfully, setIsVerifiedSuccessfully] = useState(false);

  // StrictMode runs the effect twice; a second concurrent confirm fails on the backend.
  const hasConfirmedRef = useRef(false);

  const confirmationCode = searchParams.get('code');

  const confirmEmail = async (confirmationCode: string) => {
    try {
      const confirmEmailResponse = await dispatch(
        authModel.thunks.confirmEmail({
          Action: {
            value: confirmationCode,
            case: 'ConfirmationCode',
          },
        }),
      ).unwrap();

      if (confirmEmailResponse.Status === AuthState_AuthStatus.SUCCESSFUL) {
        setIsVerifiedSuccessfully(true);

        setTimeout(() => navigate(PATHS.main), 2000);
      }

      if (confirmEmailResponse.Status === AuthState_AuthStatus.IN_PROGRESS) {
        setIsVerifiedSuccessfully(true);

        setTimeout(() => navigate(PATHS.finishRegistration), 2000);
      }
    } catch (error) {
      if (error instanceof ConnectError) {
        const { text, code } = safeJSONParse(error.rawMessage);

        if (text === UserBadRequestError.NON_RESENDABLE_AUTH_METHOD) {
          navigate(PATHS.main);

          return;
        }

        errorToastCaller(error);

        if (code) {
          navigate(PATHS.signUp);
        }
      }
    }
  };

  useEffect(() => {
    if (confirmationCode && !hasConfirmedRef.current) {
      hasConfirmedRef.current = true;

      confirmEmail(confirmationCode);
    }

    trackEvent({
      eventName: 'ConfirmEmail Page Viewed',
      isPageViewEvent: true,
    });
  }, []);

  if (isMobile) {
    return (
      <div className={className}>
        <MobileHeader isProtectedPages={false} />

        <div className={styles.mobileWrapper}>
          <h3 className={cn(styles.mobileHeading, 'h2m')}>
            {formatMessage({
              id: 'confirmEmail.verification',
              defaultMessage: 'Verification',
            })}
          </h3>

          <div className={styles.mobileContent}>
            <img
              src={smileCat}
              width={130}
              height={130}
              alt={formatMessage({
                id: 'imgAltText.smileCat',
                defaultMessage: 'Smile cat',
              })}
            />

            {isVerifiedSuccessfully && (
              <>
                <h5 className={cn(styles.centeredText, 'h5')}>
                  <FormattedMessage
                    id="confirmEmail.verifiedSuccessfully"
                    defaultMessage="Congrats! Your Diagnocat account is verified successfully."
                  />
                </h5>

                <Icon
                  name="check"
                  size={48}
                  className={styles.verifiedSuccessfullyIcon}
                />
              </>
            )}

            {!isVerifiedSuccessfully && confirmationCode && (
              <Spinner size={48} className={styles.spinner} />
            )}

            {!confirmationCode && (
              <p className={cn(styles.centeredText, 'p2')}>
                <FormattedMessage
                  id="confirmEmail.we_sent_link"
                  defaultMessage="We have sent you a link to verify your email {email}. Please check your mailbox."
                  values={{
                    email: email || 'your@mail.com',
                  }}
                />
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(styles.container, className)}
      data-testid={testID}
      data-section="confirm-email"
    >
      <div className={styles.wrapper}>
        <h3 className={cn(styles.heading, 'h3')}>
          <FormattedMessage
            id="confirmEmail.verification"
            defaultMessage="Verification"
          />
        </h3>

        <div className={styles.content}>
          <img
            src={smileCat}
            width={130}
            height={130}
            alt={formatMessage({
              id: 'imgAltText.smileCat',
              defaultMessage: 'Smile cat',
            })}
          />

          {isVerifiedSuccessfully && (
            <>
              <h5 className={cn(styles.centeredText, 'h5')}>
                <FormattedMessage
                  id="confirmEmail.verifiedSuccessfully"
                  defaultMessage="Congrats! Your Diagnocat account is verified successfully."
                />
              </h5>

              <Icon
                name="check"
                size={48}
                className={styles.verifiedSuccessfullyIcon}
              />
            </>
          )}

          {!isVerifiedSuccessfully && confirmationCode && (
            <Spinner size={48} className={styles.spinner} />
          )}

          {!confirmationCode && (
            <p className={cn(styles.centeredText, 'p2')}>
              <FormattedMessage
                id="confirmEmail.we_sent_link"
                defaultMessage="We have sent you a link to verify your email {email}. Please check your mailbox."
                values={{
                  email: email || 'your@mail.com',
                }}
              />
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
